import React, { useEffect } from "react";
import { AppsCircle, Chat, FacebookSquare, Package, PackageBoxDash, Setting, 
  SpringNotesWriteEdit, TimeClock, User, Map, Auto } from "react-huge-icons/outline";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "../../../stores/authStore";
import { ConstantRoutes } from "../../../core/constantRoutes";
import { useWorktimeStore } from "../../../stores/worktimeStore";
import useLeftMenuStore from "../../../stores/leftMenuStore";
import Dropdown from "../../dropdown/dropdown";
import AuthServiceInstance from "../../../api/authServices";
import MenuItem from "../../leftMenu/menuItem";
import CollapsibleMenuItem from "../../leftMenu/collapsibleMenuItem";
import EmailInitial from "../../common/emailInitial";
import { MainLogoNew } from "../../mainLogoNew";
import { MailNotification } from "react-huge-icons/solid";

function AdminLeftMenu() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const { isAuthenticated, authorizedEmail } = useAuthStore();
  const { worktimes, fetchWorktimes } = useWorktimeStore();
  const { isOpen, setIsOpen } = useLeftMenuStore();

  useEffect(() => {
    fetchWorktimes();
  }, []);

  const handleItemClick = (route: string) => {
    setIsOpen(false);
    navigate(route);
  };

  const mainMenuItems = [
    {
      text: t("Users"),
      icon: User,
      route: ConstantRoutes.adminUsers,
    },
    {
      text: t("OfflineService"),
      icon: Package,
      route: ConstantRoutes.adminOfflineService,
    },
    {
      text: t("Admin online service"),
      icon: PackageBoxDash,
      route: ConstantRoutes.adminOnlineService,
    },
    {
      text: t("EmailSettings"),
      icon: MailNotification,
      route: ConstantRoutes.adminEmailSettings,
    },
    {
      text: t("Support Center"),
      icon: Chat,
      route: ConstantRoutes.adminSupportCenter,
    },
    {
      text: t("Work Time"),
      icon: TimeClock,
      route: ConstantRoutes.adminWorkTime,
    },
    {
      text: t("Core"),
      icon: AppsCircle,
      route: ConstantRoutes.adminCore,
    },
  ];

  const extraMenuItems = [
    {
      text: t("Allowed Sizes"),
      icon: Setting,
      route: ConstantRoutes.adminAllowedSizes,
    },
    {
      text: t("Compares"),
      icon: Setting,
      route: ConstantRoutes.adminCompares,
    },
    {
      text: t("Signatures"),
      icon: Setting,
      route: ConstantRoutes.adminSignatures,
    },
    {
      text: t("Swid"),
      icon: Setting,
      route: ConstantRoutes.adminSwid,
    },
    {
      text: t("Groups"),
      icon: Setting,
      route: ConstantRoutes.adminGroups,
    },
    {
      text: t("Capacity table"),
      icon: Setting,
      route: ConstantRoutes.adminCapacityTable,
    },
    {
      text: t("FAQ"),
      icon: FacebookSquare,
      route: ConstantRoutes.adminFaq,
    },
    {
      text: t("News"),
      icon: SpringNotesWriteEdit,
      route: ConstantRoutes.adminNews,
    },
    {
      text: t("Offline Settings"),
      icon: Setting,
      route: ConstantRoutes.adminOfflineSettings,
    },
    {
      text: t("Remapping"),
      icon: Map,
      route: ConstantRoutes.adminRemappingSettings,
    },
    {
      text: t("Engines"),
      icon: Auto,
      route: ConstantRoutes.adminEnginesSettings,
    },
    {
      text: t("ECUs"),
      icon: Auto,
      route: ConstantRoutes.adminEcus,
    },    
  ];

  return (
    <div
      className={`flex flex-col h-screen sticky top-0 ${
        isOpen ? "translate-x-0 " : "max-md:-translate-x-full"
      } transition-transform duration-300 ease-in-out md:translate-x-0`}
    >
      <div
        className={`flex-grow bg-content2 rounded-xl overflow-hidden min-h-screen ${
          isOpen ? "md:w-65 max-md:w-96" : "max-md:w-0"
        } transition-all duration-300 ease-in-out`}
      >
        <div className="max-md:hidden">
          <MainLogoNew mode="light" onclickUrl={ConstantRoutes.main} w={230} h={65}/>
        </div>

        <div className="border border-solid border-divider" />

        {isAuthenticated() && (
          <div className="p-4 overflow-y-auto max-h-[calc(100vh-120px)]">
          {mainMenuItems.map((item) => (
            <MenuItem
              key={item.route}
              text={item.text}
              icon={item.icon}
              onClick={() => handleItemClick(item.route)}
              isSelected={location.pathname.startsWith(item.route)}
            />
          ))}
        
          <CollapsibleMenuItem
            label={t("More")}
            items={extraMenuItems}
            onItemClick={handleItemClick}
            currentPath={location.pathname}
          />
        </div>
        )}

        <div className="sticky top-[80vh]">
          <div className="md:hidden">
            {isAuthenticated() && (
              <div className="p-4">
                <div className="flex items-center bg-content3 p-4 rounded-xl">
                  <EmailInitial email={authorizedEmail} />

                  <Dropdown
                    items={[
                      { id: "profile", label: t("Profile") },
                      { id: "quit", label: t("Exit") },
                    ]}
                    onSelect={(item) => {
                      if (item.id === "quit") {
                        AuthServiceInstance.logout();
                        navigate(ConstantRoutes.main);
                      }
                    }}
                    caption={authorizedEmail ?? "ERROR"}
                    reverseDirection={true}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminLeftMenu;